import { Button, Sheet, Tooltip } from "@mui/joy";
import {
  Autocomplete,
  Box,
  Divider,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColumns,
  GridPastedValueParser,
  GridRenderEditCellParams,
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { getProjectById } from "../../kronos-kit";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { currencyFormatter, defaultDataGridProps } from "../../constants";
import { JobTask } from "../../entities/jobTask";
import { ProjectCue } from "../../entities/projectCue";
import { ProjectTask_Entity } from "../../entities/projectTask";
import { Scale_Entity } from "../../entities/scale";
import { Task } from "../../entities/task";
import { TaskType_Entity } from "../../entities/taskType";
import { sortScalesByTaskType } from "../../helpers/scaleHelpers";
// import { setSelectedGridCues } from "../../redux/cue/cueActions";
import { selectedProjectID } from "../../redux/filmsReducer";
import { useProject } from "../../redux/project/projectHooks";
import { useTalents } from "../../redux/talent/talentHooks";

import { useBudget } from "../../redux/budget/budgetHooks";
import { useJobs } from "../../redux/job/jobHooks";
import { useUpdateJobTaskMutation } from "../../redux/jobTask/jobTaskEndpoints";
import { useScales } from "../../redux/scale/scaleHooks";
import {
  useUpdateBatchTasksMutation,
  useUpdateTaskMutation,
} from "../../redux/task/taskEndpoints";
import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import TalentAvatar from "../talent/talentsList/talentAvatar";

type ScaleOption = {
  value: number;
  label: string;
};

const emptyRowSelectionModel = (): GridRowSelectionModel => ({
  type: "include",
  ids: new Set<GridRowId>(),
});

function parseClipboardNumber(input: string): number | undefined {
  const normalized = input.replace(/[$,%\s,]/g, "");
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

function parseScaleInput(input: string): number | undefined {
  const parsed = parseClipboardNumber(input);
  if (parsed === undefined) return undefined;
  return Math.round(parsed * 10000);
}

const parsePastedNumber: GridPastedValueParser = (value) =>
  parseClipboardNumber(value);
const parsePastedScale: GridPastedValueParser = (value) =>
  parseScaleInput(value);

function ScaleAutocompleteInputCell({
  id,
  field,
  value,
  options,
}: {
  id: GridRowId;
  field: string;
  value: number;
  options: ScaleOption[];
}) {
  const apiRef = useGridApiContext();
  const selectedOption = options.find((o) => o.value === value);
  const [inputValue, setInputValue] = useState(
    selectedOption ? selectedOption.label : ""
  );

  const commitScale = async (nextScale: number) => {
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: nextScale,
    });
    apiRef.current.stopCellEditMode({ id, field });
  };

  return (
    <Autocomplete<ScaleOption, false, true, true>
      freeSolo
      autoSelect
      forcePopupIcon
      autoHighlight
      openOnFocus
      fullWidth
      disableClearable
      options={options}
      PopperComponent={(props) => (
        <Popper {...props} placement="bottom-start" style={{ width: 280 }} />
      )}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, nextInputValue) => setInputValue(nextInputValue)}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, current) => option.value === current.value}
      onChange={async (_, option) => {
        if (!option) return;
        if (typeof option === "string") {
          const parsed = parseScaleInput(option);
          if (parsed === undefined) return;
          await commitScale(parsed);
          return;
        }
        await commitScale(option.value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          autoFocus
          onKeyDown={async (e) => {
            if (e.key !== "Enter") return;
            const parsed = parseScaleInput(inputValue);
            if (parsed === undefined) return;
            await commitScale(parsed);
          }}
        />
      )}
      sx={{
        width: "100%",
        "& .MuiInputBase-root": {
          fontSize: 14,
          px: 1,
        },
      }}
    />
  );
}

export default function OrchestratorMusicPrepSummary({
  projectTask,
}: {
  projectTask?: ProjectTask_Entity;
}) {
  const apiRef = useGridApiRef();
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { scales } = useScales();
  const { taskTypesMap } = useTaskTypes();
  const { jobsMap } = useJobs(
    {
      filters: JSON.stringify([
        {
          name: "jobs.projectID",
          value: projectID,
          comparison: "eq",
        },
      ]),
    },
    { skip: !projectID }
  );
  const [updateTask] = useUpdateTaskMutation();
  const [updateBatchTasks] = useUpdateBatchTasksMutation();
  const { talentsMap: talents } = useTalents();
  const [updateJobTask] = useUpdateJobTaskMutation();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const { pendingValue, markPending, dropPending, releaseSettled } =
    usePendingEdits();

  // Tasks and jobTasks are both keyed by their own id, so one lookup covers the
  // task columns and the per-job quantity columns.
  const serverValues = useMemo(() => {
    const map: { [id: number]: { [field: string]: unknown } } = {};
    projectTask?.cues.forEach((cue) =>
      cue.tasks.forEach((task) => {
        map[task.id] = task;
        task.jobTasks?.forEach((jobTask) => {
          map[jobTask.id] = jobTask;
        });
      })
    );
    return map;
  }, [projectTask]);

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => serverValues[Number(id)]?.[field]);
  }, [serverValues, releaseSettled]);

  const [selectedGridTasks, setSelectedGridTasks] =
    useState<GridRowSelectionModel>(emptyRowSelectionModel);
  const [bulkSelect, setTaskBulkSelect] = useState(false);

  const allTasks = {};
  projectTask?.cues.forEach((pt) => {
    pt.tasks.forEach((t) => (allTasks[t.cueID] = t));
  });

  function getOptions(
    items: Scale_Entity[],
    taskTypes: { [key: number]: TaskType_Entity },
    jobCategoryID?: number
  ): ScaleOption[] {
    const res: ScaleOption[] = [];
    sortScalesByTaskType(items, taskTypes).forEach((item) => {
      const taskType = taskTypes[item.taskTypeID];
      if (
        item.projectTypeID === project?.projectTypeID &&
        taskType?.jobCategoryID === jobCategoryID
      )
        res.push({
          value: item.rate,
          label: `${taskType.name}: ${currencyFormatter.format(
            item.rate / 10000
          )}`,
        });
    });

    return res;
  }

  const getJobTask = (jobID: number, taskID: number): JobTask | undefined => {
    for (const key in projectTask?.cues) {
      if (Object.prototype.hasOwnProperty.call(projectTask?.cues, key)) {
        const c = projectTask?.cues[key];

        for (const key in c.tasks[0].jobTasks) {
          if (Object.prototype.hasOwnProperty.call(c.tasks[0].jobTasks, key)) {
            const j = c.tasks[0].jobTasks[key];
            if (j.jobID === jobID && j.taskID === taskID) return j;
          }
        }
      }
    }

    return undefined;
  };

  const jobTasksForFirstCue: JobTask[] =
    projectTask?.cues[0].tasks[0].jobTasks ?? [];

  const getSelectedCueIds = (): GridRowId[] =>
    Array.from(apiRef.current.getSelectedRows().keys());

  // Fire the save without making the grid wait — see the note on `saveTask` in
  // musicPrepSummary.tsx. Holding the cell for a round-trip swallows whatever is
  // typed next.
  const onTaskUpdate = async (_new: any, _old: any) => {
    for (const k in _new) {
      if (Object.prototype.hasOwnProperty.call(_new, k)) {
        if (/^\d+$/.test(k)) {
          // jobTask Update
          const jobID = parseInt(k);
          const taskID = _new.taskID;
          const jobTask = getJobTask(jobID, taskID);
          if (jobTask && jobTask.quantity != _new[k]) {
            // Hold the committed value until the server reports it, so a later
            // refetch can't revert it.
            markPending(jobTask.id, "quantity", _new[k]);
            updateJobTask({
              id: jobTask.id,
              body: { quantity: _new[k] },
            })
              .unwrap()
              .catch((error) => {
                dropPending(jobTask.id, "quantity");
                reportSaveError(error);
              });
          }
        } else {
          if (_new[k] != _old[k]) {
            // task Update
            const selectedCueIDs = getSelectedCueIds();
            if (selectedCueIDs.length) {
              const batch: Partial<Task>[] = [];

              for (const cueID of selectedCueIDs) {
                const _task = allTasks[cueID as keyof typeof allTasks];
                if (!_task) continue;
                const _b: Partial<Task> = {
                  ..._task,
                  [k]: _new[k],
                };
                if (k === "scale" && _task.rate === 0) {
                  _b.rate = _new.scale;
                }
                batch.push(_b);
              }
              batch.forEach((_b) => {
                if (_b.id !== undefined) markPending(_b.id, k, _new[k]);
              });
              updateBatchTasks(batch)
                .unwrap()
                .catch((error) => {
                  batch.forEach((_b) => {
                    if (_b.id !== undefined) dropPending(_b.id, k);
                  });
                  reportSaveError(error);
                });
            } else {
              let body: Partial<Task> = {
                [k]: _new[k],
              };
              if (k === "scale") {
                body = {
                  scale: _new[k],
                  rate: _new["rate"] === 0 ? _new[k] : undefined,
                };
              }
              // Hold the committed value until the server reports it, so a later
              // refetch can't revert it.
              markPending(_new.taskID, k, _new[k]);
              updateTask({ id: _new.taskID, body })
                .unwrap()
                .catch((error) => {
                  dropPending(_new.taskID, k);
                  reportSaveError(error);
                });
            }
          }
        }
      }
    }

    return { ..._new };
  };

  const getRows = (): GridValidRowModel[] => {
    const rows = [];

    for (const k in projectTask?.cues) {
      if (Object.prototype.hasOwnProperty.call(projectTask?.cues, k)) {
        const cue: ProjectCue = projectTask?.cues[k];
        const task = cue.tasks[0];
        const row = {
          name: cue.name,
          bars: cue.bars,
          id: cue.id,
          quantity: pendingValue(task.id, "quantity", task.quantity),
          rate: pendingValue(task.id, "rate", task.rate),
          scale: pendingValue(task.id, "scale", task.scale),
          taskID: task.id,
          total: cue.total,
        };

        for (const key in jobTasksForFirstCue) {
          if (Object.prototype.hasOwnProperty.call(jobTasksForFirstCue, key)) {
            const jobTask = jobTasksForFirstCue[key];
            const job = jobsMap ? jobsMap[jobTask.jobID] : undefined;
            if (!job) continue;

            const cueJobTask = task.jobTasks[key];
            row[job.id] = pendingValue(
              cueJobTask.id,
              "quantity",
              cueJobTask.quantity
            );
          }
        }

        rows.push(row);
      }
    }

    return rows;
  };

  const getColumns = (bulkSelect: boolean): GridColumns<GridValidRowModel> => {
    const scaleOptions = getOptions(
      scales ?? [],
      taskTypesMap ?? {},
      projectTask?.id
    );
    const cols: GridColumns<GridValidRowModel> = [
      {
        field: "name",
        headerAlign: "center",
        align: "left",
        headerName: "Cue",
        editable: false,
        sortable: false,
        type: "string",
        width: 150,
        renderCell: (p) => {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flex: 1,
                width: "100%",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">{p.value}</Typography>
              <Tooltip size="sm" placement="right" title="Bars">
                <Sheet
                  variant="solid"
                  sx={{
                    p: 0.5,
                    background: "#9e9e9e",
                    borderRadius: 4,
                    height: 16,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    position: "sticky",
                    right: 0,
                  }}
                >
                  {p.row.bars}
                </Sheet>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "rate",
        headerName: "Rate",
        headerAlign: "center",
        sortable: false,
        editable: true,
        type: "number",
        pastedValueParser: parsePastedNumber,
        valueGetter: getterDivider("rate", 10000),
        valueSetter: setterDivider("rate", 10000),
        renderCell: (p) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            {p.field === "rate" && p.row.rate < p.row.scale ? (
              <Tooltip title="Rate should be higher than the scale">
                <i className="fa-regular fa-triangle-exclamation"></i>
              </Tooltip>
            ) : (
              []
            )}
            {`${currencyFormatter.format(p.value)}`.substring(1)}
          </Box>
        ),
        valueFormatter: (valueOrParams: any) => {
          const value = valueOrParams?.value ?? valueOrParams;
          return `${currencyFormatter.format(value)}`.substring(1);
        },
      },
      {
        field: "scale",
        headerName: "Scale",
        align: "right",
        headerAlign: "center",
        sortable: false,
        editable: true,
        type: "singleSelect",
        valueOptions: scaleOptions,
        pastedValueParser: parsePastedScale,
        renderEditCell: (p: GridRenderEditCellParams<number>) => (
          <ScaleAutocompleteInputCell
            id={p.id}
            field={p.field}
            value={p.row.scale}
            options={scaleOptions}
          />
        ),
        valueGetter: getterDivider("scale", 10000),
        valueFormatter: (valueOrParams: any) => {
          const value = valueOrParams?.value ?? valueOrParams;
          return `${currencyFormatter.format(value)}`.substring(1);
        },
      },
      {
        field: "total",
        headerName: "Total",
        headerAlign: "center",
        align: "right",
        sortable: false,
        editable: false,
        type: "number",
        valueGetter: getterDivider("total", 10000),
        valueSetter: setterDivider("total", 10000),
        valueFormatter: (valueOrParams: any) => {
          const value = valueOrParams?.value ?? valueOrParams;
          return `${currencyFormatter.format(value)}`.substring(1);
        },
      },
    ];

    for (const key in jobTasksForFirstCue) {
      if (Object.prototype.hasOwnProperty.call(jobTasksForFirstCue, key)) {
        const jobTask = jobTasksForFirstCue[key];

        const job = jobsMap ? jobsMap[jobTask.jobID] : undefined;
        if (!job) continue;
        const talent = talents![job.talentID];

        cols.push({
          field: `${job.id}`,
          headerAlign: "center",
          headerName: `${talent.firstName} ${talent.lastName}`,
          renderHeader: (p) => {
            return (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TalentAvatar
                  talent={talent}
                  size={22}
                  active={talent.active}
                />
                <Typography
                  variant="body2"
                  color={talent ? "textPrimary" : "textSecondary"}
                >
                  {talent
                    ? `${talent?.firstName} ${talent?.lastName}`
                    : "Double Click to assign"}
                </Typography>
              </Box>
            );
          },
          sortable: false,
          editable: !bulkSelect,
          width: 150,
          type: "number",
          pastedValueParser: parsePastedNumber,
          valueGetter: getterDivider(`${job.id}`, 100),
          valueSetter: setterDivider(`${job.id}`, 100),
        });
      }
    }

    return cols;
  };

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        rowSelection={bulkSelect}
        checkboxSelection={bulkSelect}
        onRowSelectionModelChange={(p) => setSelectedGridTasks(p)}
        rowSelectionModel={selectedGridTasks}
        isCellEditable={(params) =>
          project?.locked() ? false : params.colDef.editable ?? false
        }
        getCellClassName={(c) => {
          if (project?.locked()) return "lock";
          let className = !c.isEditable ? "cellReadOnly" : "";
          if (c.field === "rate" && c.row.rate < c.row.scale)
            className += " warning";

          return className;
        }}
        apiRef={apiRef}
        columns={getColumns(bulkSelect)}
        rows={getRows()}
        processRowUpdate={onTaskUpdate}
        onProcessRowUpdateError={reportSaveError}
        showToolbar
        slots={{
          toolbar: MusicPrepSummaryToolbar,
          noRowsOverlay: NoRowsOverlay,
          footer: MusicPrepSummaryFooter,
        }}
        slotProps={{
          toolbar: {
            setTaskBulkSelect,
            setSelectedGridTasks,
            bulkSelect,
          },
          footer: {
            projectTask,
          },
          cell: { tabIndex: 1 },
        }}
        initialState={{
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "name"],
            right: ["total"],
          },
        }}
      />
      {saveErrorSnackbar}
    </Paper>
  );
}

function MusicPrepSummaryToolbar(props: any) {
  const {
    setSelectedGridTasks,
    setTaskBulkSelect,
    bulkSelect = false,
  } = props ?? {};
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);

  return (
    <Toolbar>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
        <Button
          size="sm"
          variant={bulkSelect ? "solid" : "plain"}
          onClick={() => {
            setSelectedGridTasks?.(emptyRowSelectionModel());
            setTaskBulkSelect?.((e: boolean) => !e);
          }}
          disabled={project?.locked()}
        >
          {bulkSelect ? "Done" : "Bulk Edit"}
        </Button>
      </Box>
    </Toolbar>
  );
}

function MusicPrepSummaryFooter(props: { projectTask: ProjectTask_Entity }) {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { budget } = useBudget(projectID);

  return (
    <>
      <Divider />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pl: 2,
        }}
      >
        <Button
          onClick={() => getProjectById(projectID!)}
          variant="outlined"
          disabled={project?.locked()}
          size="small"
        >
          Calculate All Orchestrators
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* <ReconcileOrchestrator /> */}
          <Box
            sx={{
              p: 2,
              fontWeight: 600,
              display: "flex",
              gap: 1,
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            {/* {budget ? (
              <Button
                sx={{
                  color: "#2196f3",
                  borderColor: "#2196f3",
                  "&:hover": {
                    borderColor: "#2196f3",
                  },
                }}
                disabled={project?.locked()}
                onClick={() =>
                  dispatch(setFormOpen(true, "ReconcileOrchestrator"))
                }
                variant="outlined"
                size="small"
              >
                Reconcile{" "}
                <i className="fa-solid fa-wand-magic-sparkles pleft"></i>
              </Button>
            ) : (
              []
            )} */}
            Subtotal:{" "}
            {currencyFormatter.format(props?.projectTask?.subTotal / 10000)}
          </Box>
        </Box>
      </Box>
    </>
  );
}
